import { createSignal, onMount, createEffect, Show, on, untrack, onCleanup } from "solid-js";
import { useActiveManeuverViewMirror } from "../../../../store/activeManeuverViewStore";
import { createMemo } from "solid-js";

import DropDownButton from "../../../../components/buttons/DropDownButton";

import DataTable_Big from "../../../../components/maneuvers/standard/DataTable_Big";
import DataTable_Small from "../../../../components/maneuvers/standard/DataTable_Small";
import ManeuverMap from "../../../../components/maneuvers/standard/Map";
import Video from "../../../../components/maneuvers/standard/Video";
import Scatter from "../../../../components/maneuvers/standard/Scatter";
import TimeSeries, { MAP_SIDE_PANEL_EXTRA_HEIGHT_PX, MAP_SIDE_PANEL_BUTTON_GAP_PX } from "../../../../components/maneuvers/standard/TimeSeries";
import DataTable_BigGrouped from "../../../../components/maneuvers/grouped/DataTable_Big";
import DataTable_SmallGrouped from "../../../../components/maneuvers/grouped/DataTable_Small";
import MapGrouped from "../../../../components/maneuvers/grouped/Map";
import ScatterGrouped from "../../../../components/maneuvers/grouped/Scatter";
import TimeSeriesGrouped from "../../../../components/maneuvers/grouped/TimeSeries";
import ScatterTimeseries from "../../../../components/charts/ScatterTimeseries";
import * as d3 from "d3";

import { setupMediaContainerScaling } from "../../../../utils/global";
import { logPageLoad, logActivity, getCurrentProjectDatasetIds } from "../../../../utils/logging";
import { error as logError, debug as logDebug } from "../../../../utils/console";
import { normalizeManeuverEventType } from "../../../../utils/maneuverEventType";
import { clearManeuverSelectionOnEventTypeChange } from "../../../../utils/maneuverEventTypeSelection";
import { useOpenGradeAllForMapClick } from "../../../../utils/maneuverMapClickGrade";

import { phase, setPhase, color, setColor, tws, setTws, eventType, setEventType, grouped, groupDisplayMode, setGroupDisplayMode, maneuvers, setManeuvers, setTableData, setFiltered, filtered, tabledata, maneuverTableSort, setManeuverTableSort, resetManeuverTableSort } from "../../../../store/globalStore";
import { sortManeuverRows } from "../../../../utils/maneuverTableSort";
import { selectedEvents, setSelectedEvents, setLegendSelectionActive, setTriggerUpdate, setTriggerSelection, setSelection, selection, hasSelection, isCut, cutEvents, hideSelectedEvents, selectedGroupKeys, setSelectedGroupKeys, setSelectedRanges, selectedRanges, clearSelection, cutSelection, setHasSelection } from "../../../../store/selectionStore";
import InsightPanel from "../../../../components/insights/InsightPanel";
import {
  buildManeuverFindings,
  resolveHomeBoatName,
  type ShowMeAction,
} from "../../../../insights";
import { scrollToEvidence } from "../../../../utils/scrollToEvidence";
import {
  selectionRangesSignatureForEvents,
  buildSelectedRangesForEvents,
  extractRowSourceId,
  extractRowSourceName,
  findManeuverRowByEventId,
  applyCutToRows,
  type SelectionRangeColorResolver,
} from "../../../../utils/selectionRangeHelpers";

import { persistantStore } from "../../../../store/persistantStore";
import { sourcesStore } from "../../../../store/sourcesStore";
import { apiEndpoints } from "@config/env";
import { getData, getEventTimes, formatDateTime } from "../../../../utils/global";
import { runEventTimesSelectionSync } from "../../../../utils/eventTimesSelection";
import ManeuverSettings from "../../../../components/menus/ManeuverSettings";
import PerformanceFilterSummary from "../../../../components/legends/PerformanceFilterSummary";
import { persistentSettingsService } from "../../../../services/persistentSettingsService";
import { getProjectManeuverFilters } from "../../../../services/projectFiltersService";
import { user } from "../../../../store/userStore";
import { debug } from "../../../../utils/console";
import { useDashboardShareRestore, willRestoreShare, shareRestoreReady } from "../../../../hooks/useDashboardShareRestore";
import { useManeuverStatesForEventType } from "../../../../utils/maneuverStateReconcile";
import { buildManeuversShareState, applyManeuversShareState, currentManeuverBaselineShare, consumeShareSpecifiedBaseline } from "../../../../utils/reportShareState";
import {
  setSelectedGradesManeuvers,
  setSelectedStatesManeuvers,
  setSelectedRacesManeuvers,
  setSelectedLegsManeuvers,
  maneuverTrainingRacing,
  setManeuverTrainingRacing,
  maneuverTackEntry,
  setManeuverTackEntry,
  raceOptions as storeRaceOptions,
  legOptions as storeLegOptions,
  gradeOptions as storeGradeOptions,
  setRaceOptions as pushRaceOptionsToFilterStore,
  setLegOptions as pushLegOptionsToFilterStore,
  setGradeOptions as pushGradeOptionsToFilterStore,
} from "../../../../store/filterStore";
import { setCurrentDataset, getCurrentDatasetTimezone } from "../../../../store/datasetTimezoneStore";
import { getGroupKeyFromItem, groupKeyEquals, legendTextToGroupKeyTable, toggleGroupKeyInList } from "../../../../utils/colorGrouping";
import { mediaAvailabilityService } from "../../../../services/mediaAvailabilityService";
import { MANEUVER_VIDEO_START_OFFSET_SECONDS, clearManeuverPlayhead } from "../../../../store/playbackStore";
import { TAKEOFF_CHANNELS, ACCELERATION_ACCMAX_CHANNELS, ACCELERATION_BUILD_VMG_CHANNELS, RIDE_HEIGHT_CHANNELS, buildManeuversChannelsRequest } from "../../../../utils/maneuversConfig";
import {
  mapManeuverRowsToScatterTimeseriesPoints,
  maneuverTimelineLegendGroups,
} from "../../../../utils/maneuverScatterTimeseries";
import ManeuverColorLegend from "../../../../components/legends/ManeuverColorLegend";
import {
  buildManeuverColorLegendItems,
  applyManeuverLegendSelection,
} from "../../../../utils/maneuverColorLegend";
import { maneuverColorLegendReservePx } from "../../../../utils/maneuverLegendHeight";
import { maneuverBaselineLabel } from "../../../../utils/maneuverBaselineLegend";
import { maneuverBaselineStore } from "../../../../store/maneuverBaselineStore";
import BaselineDisplayModal from "../../../../components/modals/BaselineDisplayModal";
import { useManeuverBaselineDisplay } from "../../../../hooks/useManeuverBaselineDisplay";
import { useManeuverBaselineLoader } from "../../../../utils/maneuverBaseline";
import { applyManeuverEntrySide } from "../../../../utils/maneuverTackEntryFilter";
import { passesManeuverRaceFilter, passesManeuverTrainingRacingFilter } from "../../../../utils/maneuverRaceFilter";
import { applyManeuverFoilingStateAfterFetch, reconcileManeuverStatesWithBaseline } from "../../../../utils/maneuverFoilingState";
import { fetchFleetDayManeuverStateOptions } from "../../../../utils/maneuverStateOptionsService";

const { selectedClassName, selectedProjectId, selectedDate, selectedDatasetId } = persistantStore;

// Channels list for maneuvers endpoints - matches single-source Maneuvers / ManeuversHistory (incl. drop columns)
const MANEUVERS_CHANNELS = [
  'Tws_bin',
  'Tws_avg',
  'Twa_entry',
  'Twa_entry_n',
  'Vmg_perc_avg',
  'Loss_inv_tgt',
  'Loss_turn_tgt',
  'Loss_build_tgt',
  'Loss_total_tgt',
  'Mmg',
  'Bsp_drop',
  'Bsp_min',
  'Bsp_min_delta',
  'Drop_time',
  'Pop_time',
  'Time_two_boards',
  'Raise_time',
  'Accel_slope',
  'Decel_slope',
  'Lwy_max',
  'Turn_radius',
  'Accel_max',
  'Accel_min',
  'Twa_exit_n',
  'Twa_build_n',
  'Twa_drop_n',
  'Turn_rate_max',
  'Turn_angle_max',
  'Overshoot_angle',
  'Overshoot_perc',
  'Time_raising',
  'Time_dropping',
  'Rud_ang_max',
  'Twd_delta',
  'Rake_drop',
  'Aoa_drop',
  'Heel_drop',
  'Rake_aoa_min_old_turn',
  'Aoa_raise',
  'Pitch_raise',
  'Heel_lock',
  'Rake_min_old_turn',
  'Rake_max_new_turn',
  'Rake_removed_old_time_turn',
  'Rake_added_new_time_turn',
  'Cant_drop_tgt',
  ...ACCELERATION_BUILD_VMG_CHANNELS,
  ...ACCELERATION_ACCMAX_CHANNELS,
  ...RIDE_HEIGHT_CHANNELS,
];

interface FleetReportSplitViewProps {
  isInSplitView?: boolean;
  mainPanelMenu?: string | null;
  rightPanelMenu?: string | null;
}

export default function FleetManeuversPage(props: FleetReportSplitViewProps = {}) {
  // Use fleet context for this component
  const context = 'fleet';

  const baselineDisplay = useManeuverBaselineDisplay({
    getClassName: () => selectedClassName(),
    getProjectId: () => selectedProjectId(),
    getEventType: () => eventType(),
    onApplied: () => setTriggerUpdate(true),
  });
  // Same overlay as the dataset page: TWS ALL draws the scatter curve; a bin draws map and timeseries.
  useManeuverBaselineLoader({ tws, eventType, context });

  const [eventTypes] = createSignal<string[]>(['TACK','GYBE','ROUNDUP','BEARAWAY','TAKEOFF']);
  const [phases] = createSignal<string[]>(['FULL','INVESTMENT','TURN','ACCELERATION']);
  // For fleet context, allow coloring by SOURCE, TACK, CONFIG, STATE, and RACE.
  // EVENT (regatta) is a history-only option (ManeuversHistory / FleetManeuversHistory); the day report colors by RACE.
  const [colors] = createSignal<string[]>(['SOURCE', 'TACK', 'CONFIG', 'STATE', 'RACE', 'TESTS']);
  const [grades] = createSignal<string[]>(['ALL','> 1','1','2','3']);
  const [twsoptions, setTwsOptions] = createSignal<string[]>([]);

  const [views] = createSignal<string[]>(['MAP','TABLE','SCATTER','TIME SERIES']);
  const [view, setView] = createSignal<'MAP' | 'TABLE' | 'SCATTER' | 'TIME SERIES' | 'VIDEO'>('MAP');
  useActiveManeuverViewMirror(view);
  // VIDEO mode: toggle the left column between the data table and a playhead-synced timeseries.
  // Ephemeral — reset back to TABLE whenever we leave VIDEO.
  const [videoSidePanel, setVideoSidePanel] = createSignal<'TABLE' | 'CHART'>('TABLE');
  createEffect(on(view, (v) => { if (v !== 'VIDEO') { setVideoSidePanel('TABLE'); } }));
  // Clearing the selection returns the video panel to the data table (the chart needs a selection).
  createEffect(() => { if (videoSidePanel() === 'CHART' && selectedEvents().length === 0) { setVideoSidePanel('TABLE'); } });
  // MAP mode: same toggle, but the playhead drag moves a boat on the map instead of seeking video.
  const [mapSidePanel, setMapSidePanel] = createSignal<'TABLE' | 'CHART'>('TABLE');
  createEffect(on(view, (v) => { if (v !== 'MAP') { setMapSidePanel('TABLE'); clearManeuverPlayhead(); } }));
  createEffect(() => { if (mapSidePanel() === 'CHART' && selectedEvents().length === 0) { setMapSidePanel('TABLE'); } });
  const [groupedOptions] = createSignal<string[]>(['OFF','ON','MIX']);
  const [scatterKey, setScatterKey] = createSignal<number>(0);

  /**
   * Show-me teams to apply after an event-type change refetch.
   * Cleared once applied (or when the user clears selection).
   */
  let pendingShowMeTeams: string[] | null = null;
  let pendingScrollToEvidence = false;

  // State for ManeuverSettings
  const [raceOptions, setRaceOptions] = createSignal<(number | string)[]>([]);
  const [legOptions, setLegOptions] = createSignal<number[]>([]);
  const [gradeOptions, setGradeOptions] = createSignal<number[]>([]);
  const [stateOptions, setStateOptions] = createSignal<string[]>([]);
  const [selectedRaces, setSelectedRaces] = createSignal<(number | string)[]>([]);
  const [selectedLegs, setSelectedLegs] = createSignal<number[]>([]);
  const [selectedGrades, setSelectedGrades] = createSignal<number[]>([]);
  const [selectedStates, setSelectedStates] = createSignal<string[]>([]);
  /**
   * True when the maneuver type on screen has a REMEMBERED state selection, which is what stops
   * option discovery replacing it with "every state this type has". Owned by
   * useManeuverStatesForEventType below — the answer is per event type, because the vocabularies are
   * disjoint (H-ladder vs round up / bear away geometry).
   */
  let hasSavedStateFilter = false;

  const { persistStates, applyProjectDefaultStates } = useManeuverStatesForEventType({
    eventType,
    selectedStates,
    applySelection: (next) => {
      setSelectedStates(next);
      setSelectedStatesManeuvers(next);
    },
    setHasSavedStates: (value) => { hasSavedStateFilter = value; },
    userId: () => user()?.user_id ?? '',
    className: () => selectedClassName() ?? '',
    projectId: () => selectedProjectId() ?? 0,
    suppressInitialRestore: () => willRestoreShare('FleetManeuvers'),
  });
  
  // Source selection state
  const [selectedSources, setSelectedSources] = createSignal<Set<number>>(new Set());

  const [showTimeline, setShowTimeline] = createSignal<boolean>(true);
  const handleTimelineChange = (value: boolean) => {
    setShowTimeline(value);
  };

  const FLEET_MANEUVERS_PAGE_KEY = 'FleetManeuvers';
  useDashboardShareRestore(
    FLEET_MANEUVERS_PAGE_KEY,
    () =>
      buildManeuversShareState({
        ...currentManeuverBaselineShare(),
        view: view(),
        showTimeline: showTimeline(),
        eventType: eventType(),
        phase: phase(),
        color: color(),
        tws: tws(),
        grouped: grouped(),
        groupDisplayMode: groupDisplayMode(),
        selectedRaces: selectedRaces(),
        selectedLegs: selectedLegs(),
        selectedGrades: selectedGrades(),
        selectedStates: selectedStates(),
        selectedSourceIds: Array.from(selectedSources()),
        maneuverTrainingRacing: maneuverTrainingRacing(),
        maneuverTackEntry: maneuverTackEntry(),
        selectedEvents: selectedEvents(),
        hasSelection: hasSelection(),
        maneuverSelection: selection(),
      }),
    (obj) =>
      applyManeuversShareState(obj, FLEET_MANEUVERS_PAGE_KEY, {
        setView,
        setShowTimeline,
        setSelectedRaces,
        setSelectedLegs,
        setSelectedGrades,
        setSelectedStates,
        setSelectedSourceIds: (value: unknown) => setSelectedSources(value as Set<number>),
        applySharedBaseline: (value: unknown) => { void baselineDisplay.showSharedBaseline(value); },
      })
  );

  const fleetManeuverSourceColorScale = createMemo(() => {
    const sources = sourcesStore.sources();
    const isReady = sourcesStore.isReady();
    if (!isReady || sources.length === 0) return null;
    const sourceNames = sources.map((s) => String(s.source_name).toLowerCase());
    const colorsArr = sources.map((s) => sourcesStore.getSourceColor(s.source_name) || '#1f77b4');
    return d3.scaleOrdinal<string, string>().domain(sourceNames).range(colorsArr).unknown('#1f77b4');
  });

  // Memoize view options. VIDEO only when group is OFF (not ON/MIX).
  const viewOptions = createMemo<string[]>(() => {
    const base = grouped() ? ['MAP','TABLE','BOXES','TIME SERIES'] : views();
    if (grouped()) {
      return base;
    }
    return [...base, 'VIDEO'];
  });

  // Abort controllers for canceling requests
  let tableDataController: AbortController | null = null;
  let timesController: AbortController | null = null;
  let stateOptionsController: AbortController | null = null;

  const applyDiscoveredStateOptions = (discovered: string[]) => {
    if (discovered.length === 0) return;
    setStateOptions(discovered);
    const reconciled = reconcileManeuverStatesWithBaseline(
      selectedStates(),
      discovered,
      hasSavedStateFilter
    );
    if (
      reconciled.length !== selectedStates().length ||
      reconciled.some((s, i) => s !== selectedStates()[i])
    ) {
      setSelectedStates(reconciled);
      setSelectedStatesManeuvers(reconciled);
    }
  };

  const refreshManeuverStateOptions = async () => {
    const requestEventType = normalizeManeuverEventType(eventType());
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const date = (selectedDate() || '').trim();
    if (!className || !projectId || !date) return;

    const sources = sourcesStore.sources() ?? [];
    if (!sourcesStore.isReady() || sources.length === 0) return;

    const selectedSourceIds = selectedSources();
    const sourceNames = sources
      .filter((s: { source_id?: number; source_name?: string }) =>
        selectedSourceIds.size === 0 || (s.source_id != null && selectedSourceIds.has(s.source_id))
      )
      .map((s: { source_name?: string }) => s.source_name)
      .filter((name): name is string => typeof name === 'string' && name.trim() !== '');

    if (stateOptionsController) stateOptionsController.abort();
    stateOptionsController = new AbortController();

    const discovered = await fetchFleetDayManeuverStateOptions({
      className,
      projectId,
      date,
      eventType: requestEventType,
      sourceNames,
      signal: stateOptionsController.signal,
    });
    if (normalizeManeuverEventType(eventType()) !== requestEventType) return;
    applyDiscoveredStateOptions(discovered);
  };

  /** After onMount applies project/persistent filters — prevents createEffects from racing ahead and double-fetching. */
  const [fleetMountComplete, setFleetMountComplete] = createSignal(false);
  let fleetTableFetchDebounce: ReturnType<typeof setTimeout> | null = null;
  let lastFetchedEventType: string | null = null;

  /** Boat name on a maneuver row — source_name preferred, TEAM as fallback (matches insight getTeamName). */
  const rowTeamName = (row: Record<string, unknown>): string => {
    const fromSource = extractRowSourceName(row);
    if (fromSource) return fromSource;
    const team = row.TEAM ?? row.team ?? row.Team;
    if (typeof team === 'string' && team.trim()) return team.trim();
    return '';
  };

  const rowEventId = (row: Record<string, unknown>): number | null => {
    const raw = row.event_id ?? row.Event_id ?? row.EVENT_ID;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  /**
   * Insight Show me: select maneuvers for the given boat names.
   * Ungrouped mode uses event IDs (what map/table highlight); grouped mode uses group keys.
   *
   * Must not run in the same turn as clearSelection/setSelectedEvents([]) — selectionStore's
   * isUpdatingSelectedEvents guard drops the follow-up write until its follow-up microtask finishes.
   */
  const applyShowMeTeamSelection = (rows: Record<string, unknown>[]) => {
    const pending = pendingShowMeTeams;
    pendingShowMeTeams = null;
    if (!pending || pending.length === 0) {
      return;
    }
    const wanted = new Set(pending.map((t) => t.trim().toLowerCase()).filter(Boolean));
    if (wanted.size === 0) {
      return;
    }

    // Prefer visible table rows; fall back to full maneuvers if the table is empty or unmatched.
    const primary = Array.isArray(rows) && rows.length > 0 ? rows : [];
    const fallback = (maneuvers() ?? []) as Record<string, unknown>[];
    const pool = primary.length > 0 ? primary : fallback;

    if (grouped()) {
      const keys: string[] = [];
      const seen = new Set<string>();
      for (const row of pool) {
        const name = rowTeamName(row);
        if (!name) continue;
        const lower = name.toLowerCase();
        if (!wanted.has(lower) || seen.has(lower)) continue;
        seen.add(lower);
        keys.push(name);
      }
      const next = keys.length > 0 ? keys : pending.map((t) => t.trim()).filter(Boolean);
      setSelectedGroupKeys(next);
      setHasSelection(next.length > 0);
      setTriggerSelection(true);
      logDebug('FleetManeuvers: Show me selected group keys', next);
      return;
    }

    const collectIds = (list: Record<string, unknown>[]): number[] => {
      const ids: number[] = [];
      for (const row of list) {
        const name = rowTeamName(row);
        if (!name || !wanted.has(name.toLowerCase())) continue;
        const id = rowEventId(row);
        if (id != null) ids.push(id);
      }
      return Array.from(new Set(ids));
    };

    let unique = collectIds(pool);
    if (unique.length === 0 && fallback.length > 0 && fallback !== pool) {
      unique = collectIds(fallback);
    }

    // defer:false — write now; banner/map/table read selectedEvents synchronously.
    setSelectedEvents(unique, { defer: false });
    setHasSelection(unique.length > 0);
    setLegendSelectionActive(unique.length > 0);
    setTriggerSelection(true);
    setTriggerUpdate(true);
    if (unique.length === 0) {
      logDebug('FleetManeuvers: Show me found no event IDs for teams', {
        teams: pending,
        poolSize: pool.length,
        sampleNames: pool.slice(0, 5).map(rowTeamName),
      });
    } else {
      logDebug('FleetManeuvers: Show me selected event IDs', { count: unique.length, teams: pending });
    }
  };

  const fetchTableData = async () => {
    // Cancel previous request if still pending
    if (tableDataController) {
      tableDataController.abort();
    }

    // Create new abort controller
    tableDataController = new AbortController();

      // Capture event type for this request so we don't apply a stale response over current type
      const requestEventType = normalizeManeuverEventType(eventType());
      const eventTypeChanged = requestEventType !== lastFetchedEventType;

      if (requestEventType === 'TAKEOFF') {
        logDebug('FleetManeuvers: fetchTableData for TAKEOFF', { date: selectedDate(), sourcesCount: sourcesStore.sources()?.length ?? 0 });
      }

    try {
      // Get date and sources
      const date = selectedDate();
      if (!date || date.trim() === '') {
        // Persistent settings merges can briefly clear selectedDate in localStorage; do not wipe in-memory maneuvers
        // (that unmounts the map and causes duplicate fetchMapData).
        if (untrack(() => maneuvers().length > 0) && !eventTypeChanged) {
          logDebug('FleetManeuvers: fetchTableData skipped — empty date while maneuvers loaded (store settling)');
          return;
        }
        setManeuvers([]);
        setTableData([]);
        setFiltered([]);
        setTwsOptions(['ALL']);
        return;
      }

      const sourcesReady = sourcesStore.isReady();
      const sources = sourcesStore.sources();
      if (!sourcesReady || !sources || sources.length === 0) {
        if (untrack(() => maneuvers().length > 0) && !eventTypeChanged) {
          logDebug('FleetManeuvers: fetchTableData skipped — sources not ready while maneuvers loaded');
          return;
        }
        setManeuvers([]);
        setTableData([]);
        setFiltered([]);
        setTwsOptions(['ALL']);
        return;
      }

      // Fetch data from selected sources and aggregate
      const allData: any[] = [];

      // Build filters so user's grade choice (e.g. All / >0) is applied server-side; without this, backend defaults to grade > 1
      const filters: Record<string, number[] | string[]> = {};
      const grades = selectedGrades();
      if (grades.length > 0) {
        filters.GRADE = grades.map(g => Number(g)).filter(n => !isNaN(n));
      }
      // Training/Racing filter: pass to API so SQL can filter (racing = exclude training, training = only training)
      const trainingRacingVal = maneuverTrainingRacing();
      if (trainingRacingVal === 'TRAINING' || trainingRacingVal === 'RACING') {
        filters.TRAINING_RACING = [trainingRacingVal];
      }
      // For TAKEOFF, request only takeoff-relevant channels (not tack/gybe columns)
      const channelsToRequest = buildManeuversChannelsRequest(requestEventType, MANEUVERS_CHANNELS, {
        takeoffPrefix: ['twa_entry'],
      });
      let baseUrl = `${apiEndpoints.app.data}/maneuvers?class_name=${encodeURIComponent(selectedClassName())}&project_id=${encodeURIComponent(selectedProjectId())}&date=${encodeURIComponent(date)}&event_type=${encodeURIComponent(requestEventType)}&channels=${encodeURIComponent(JSON.stringify(channelsToRequest))}`;
      if (Object.keys(filters).length > 0) {
        baseUrl += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
      }

      // Filter sources to only fetch from selected ones
      const selectedSourceIds = selectedSources();
      const sourcesToFetch = selectedSourceIds.size > 0 
        ? sources.filter((s: any) => selectedSourceIds.has(s.source_id))
        : sources; // If no sources selected, fetch from all (default behavior)

      // One request for all selected sources — /maneuvers takes source_names as an array and returns
      // d.source_name on every row, so there is no reason to fan out per source.
      const canonicalSourceNames = new Map<string, string>(
        sourcesToFetch.map((s: any) => [String(s.source_name).trim().toLowerCase(), s.source_name])
      );
      const rows = await (async (): Promise<any[]> => {
        // No matching sources: skip the request entirely. The endpoint rejects an empty
        // source_names array, and the per-source fan-out this replaced simply made no calls.
        if (sourcesToFetch.length === 0) return [];
        try {
          const sourceNames = sourcesToFetch.map((s: any) => s.source_name);
          const url = `${baseUrl}&source_names=${encodeURIComponent(JSON.stringify(sourceNames))}`;
          const result = await getData(url, tableDataController!.signal);

          if (result.success && result.data && Array.isArray(result.data)) {
            // Normalize the row's own source_name to the sources-store casing so downstream
            // per-team matching (colours, selection, map attribution) is unaffected.
            return result.data.map((item: any) => ({
              ...item,
              source_name: canonicalSourceNames.get(String(item.source_name).trim().toLowerCase()) ?? item.source_name
            }));
          }
          return [];
        } catch (error: unknown) {
          if (error instanceof Error && error.name === 'AbortError') {
            return [];
          }
          logError('Error fetching fleet maneuver data:', error as any);
          return [];
        }
      })();

      if (requestEventType.toUpperCase() === 'TAKEOFF') {
        const rowsBySource = new Map<string, number>();
        for (const row of rows) {
          const name = String(row?.source_name ?? '');
          rowsBySource.set(name, (rowsBySource.get(name) ?? 0) + 1);
        }
        logDebug('FleetManeuvers: TAKEOFF fetchTableData results', {
          sourceCounts: [...rowsBySource].map(([source, count]) => ({ source, rows: count })),
          totalRows: rows.length
        });
      }

      // Ignore response if user changed maneuver type before this request completed (avoid stale overwrite)
      if (normalizeManeuverEventType(eventType()) !== requestEventType) {
        return;
      }

      // Combine all data from all sources
      allData.push(...rows);

      // Add datetimeLocal (dataset local time) for table and tooltips
      allData.forEach((item: any) => {
        const tz = (item.timezone ?? item.Timezone ?? "").trim() || undefined;
        const rawDt = item.Datetime ?? item.datetime;
        if (rawDt && tz) {
          try {
            const formatted = formatDateTime(rawDt, tz);
            if (formatted) item.datetimeLocal = formatted;
          } catch {
            // leave datetimeLocal undefined
          }
        }
      });

      if (allData.length > 0) {
        // TAKEOFF, BEARAWAY, ROUNDUP: Port/Stbd from entry or build TWA; otherwise by phase
        const evt = (requestEventType || '').toUpperCase();
        const usePortStbd = evt === 'TAKEOFF' || evt === 'BEARAWAY' || evt === 'ROUNDUP';
        if (usePortStbd) {
          allData.forEach((item: any) => {
            const twa = item.twa_entry ?? item.twa_build ?? item.Twa_start ?? 0;
            item.tack = Number(twa) > 0 ? 'STBD' : 'PORT';
          });
        } else if (phase() == 'FULL' || phase() == 'TURN') {
          allData.forEach((item: any) => {
            item.tack = item.twa_entry > 0 ? 'S - P' : 'P - S';
          });
        } else if (phase() == 'INVESTMENT') {
          allData.forEach((item: any) => {
            item.tack = item.twa_entry > 0 ? 'STBD' : 'PORT';
          });
        } else {
          allData.forEach((item: any) => {
            item.tack = item.twa_entry > 0 ? 'PORT' : 'STBD';
          });
        }

        // Generate TWS bins in intervals of 5
        // Extract all TWS values from data
        const twsValues: number[] = [];
        allData.forEach((item: any) => {
          const twsBin = item.tws_bin ?? item.tws_avg;
          if (twsBin !== null && twsBin !== undefined && !isNaN(Number(twsBin))) {
            twsValues.push(Number(twsBin));
          }
        });
        
        // Generate bins in increments of 5 starting from the minimum
        let tws_bins: string[] = ['ALL'];
        if (twsValues.length > 0) {
          const minTws = Math.min(...twsValues);
          const maxTws = Math.max(...twsValues);
          
          // Round down to nearest multiple of 5 for start
          const startBin = Math.floor(minTws / 5) * 5;
          // Round up to nearest multiple of 5 for end, then add one more increment
          const endBin = Math.ceil(maxTws / 5) * 5 + 5;
          
          // Generate bins in increments of 5
          for (let bin = startBin; bin <= endBin; bin += 5) {
            tws_bins.push(String(bin));
          }
        }
        setTwsOptions(tws_bins)

        const baseline = stateOptions();
        const { reconciled } = applyManeuverFoilingStateAfterFetch(
          allData,
          selectedStates(),
          hasSavedStateFilter,
          { availableStates: baseline }
        );
        if (
          reconciled.length !== selectedStates().length ||
          reconciled.some((s, i) => s !== selectedStates()[i])
        ) {
          setSelectedStates(reconciled);
          setSelectedStatesManeuvers(reconciled);
        }

        setManeuvers(allData)
        lastFetchedEventType = requestEventType;

        // FleetManeuvers: seed selection from full API list, then filterData sets filtered/tabledata/selection when !hasSelection
        const eventIdsFromFetch = allData.map((item: any) => item.event_id);
        setSelection(eventIdsFromFetch as any);

        // Pass data directly to filterData — sets filtered(), tabledata, triggerUpdate
        filterData(allData)

        // Insight Show me after event-type change: select matching boats once rows are ready.
        // Defer one tick so any selectionStore clear from handleEventType has released its guard.
        if (pendingShowMeTeams) {
          const showMeRows = allData as Record<string, unknown>[];
          const shouldScroll = pendingScrollToEvidence;
          setTimeout(() => {
            if (!pendingShowMeTeams) {
              return;
            }
            applyShowMeTeamSelection(showMeRows);
            if (shouldScroll) {
              pendingScrollToEvidence = false;
              scrollToEvidence(
                "[data-fleet-maneuvers-page] .maneuver-map-layout, [data-fleet-maneuvers-page] #table-area"
              );
            }
          }, 0);
        }
      } else {
        // Handle empty data - reset to empty state only if this response is still current
        if (normalizeManeuverEventType(eventType()) === requestEventType) {
          if (requestEventType === 'TAKEOFF') {
            logDebug('FleetManeuvers: TAKEOFF table/scatter received no rows from API');
          }
          setManeuvers([]);
          setTableData([]);
          setFiltered([]);
          setTwsOptions(['ALL']);
          lastFetchedEventType = requestEventType;
          if (pendingShowMeTeams) {
            applyShowMeTeamSelection([]);
            pendingScrollToEvidence = false;
          }
        }
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Request was cancelled - this is expected, no error needed
      } else {
        if (normalizeManeuverEventType(eventType()) === requestEventType) {
          logError('Error fetching table data:', error as any);
          setManeuvers([]);
          setTableData([]);
          setFiltered([]);
          setTwsOptions(['ALL']);
          lastFetchedEventType = requestEventType;
          if (pendingShowMeTeams) {
            applyShowMeTeamSelection([]);
            pendingScrollToEvidence = false;
          }
        }
      }
    } finally {
      tableDataController = null;
    }
  };

  const scheduleFleetTableFetch = () => {
    if (fleetTableFetchDebounce) clearTimeout(fleetTableFetchDebounce);
    fleetTableFetchDebounce = setTimeout(() => {
      fleetTableFetchDebounce = null;
      void fetchTableData();
    }, 100);
  };

  const handleEventType = (val: string): void => {
    const prev = normalizeManeuverEventType(eventType());
    const next = normalizeManeuverEventType(val);
    if (next === prev) return;
    setEventType(next);
    clearManeuverSelectionOnEventTypeChange('FleetManeuvers');
    // States are swapped by useManeuverStatesForEventType, which restores the incoming type's own
    // chips. The TAKEOFF special case that used to live here re-ran the whole settings load on the
    // way out, which also re-applied the saved grade / race / leg filters over whatever the user
    // had set — the state swap was never the only thing it did.
    scheduleFleetTableFetch();
  };

  const handlePhase = (val: string): void => {
    setPhase(val)
    setTriggerUpdate(true);
  };

  const handleView = async (val: string): Promise<void> => {
    const normalized = (val === 'BOXES') ? 'SCATTER' : val;
    // Guard to satisfy the view signal's union type
    if (normalized === 'MAP' || normalized === 'TABLE' || normalized === 'SCATTER' || normalized === 'TIME SERIES' || normalized === 'VIDEO') {
      setView(normalized);
      const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
      await logActivity(project_id, dataset_id, 'FleetManeuvers.tsx', 'Fleet Maneuvers Report', `View changed to ${normalized}`);
      if (normalized === 'VIDEO') {
        const dateStr = selectedDate() ?? '';
        const dateYmd = dateStr.replace(/\D/g, '').slice(0, 8);
        if (dateYmd.length >= 8) {
          const data = maneuvers();
          const seen = new Set<string>();
          const entries: Array<{ sourceName: string; dateYmd: string }> = [];
          for (const row of data ?? []) {
            const sn = row?.source_name ?? row?.sourceName ?? row?.Source_name ?? row?.source ?? '';
            if (typeof sn === 'string' && sn.trim() && !seen.has(sn.trim().toLowerCase())) {
              seen.add(sn.trim().toLowerCase());
              entries.push({ sourceName: sn.trim(), dateYmd });
            }
          }
          if (entries.length > 0) {
            await mediaAvailabilityService.preloadForSourcesAndDates(entries, getCurrentDatasetTimezone());
          }
        }
      }
      // Always re-run filterData on view change: VIDEO view filters by has-video; other views show all (grade/TWS/etc) maneuvers
      filterData();
    }
    setTriggerUpdate(true);
  };

  // Force SCATTER/BOXES content to remount and redraw when grouped toggles
  createEffect(() => {
    const g = grouped();
    if (view() === 'SCATTER') {
      setScatterKey((k: number) => k + 1);
    }
  });

  const handleColor = async (val: string): Promise<void> => {
    setColor(val)
    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
    await logActivity(project_id, dataset_id, 'FleetManeuvers.tsx', 'Fleet Maneuvers Report', `Color changed to ${val}`);
    setTriggerUpdate(true)
  };

  const filterData = (dataOverride?: any[]): void => {
    // Use provided data or read from store - this avoids race conditions
    const maneuversData = dataOverride ?? maneuvers();
    if (!maneuversData || !Array.isArray(maneuversData) || maneuversData.length === 0) {
      setFiltered([]);
      setTableData([]);
      return;
    }

    let filteredData_tws: any[] = []

    if (tws() == 'ALL') {
      filteredData_tws = maneuversData;
    } else {
      // Filter data where TWS value is within ±2.5 of the selected bin (intervals of 5)
      const selectedTws = Number(tws());
      if (!isNaN(selectedTws)) {
        filteredData_tws = maneuversData.filter((item: any) => {
          const itemTws = item.tws_bin ?? item.tws_avg;
          if (itemTws === null || itemTws === undefined || isNaN(Number(itemTws))) {
            return false;
          }
          const itemTwsNum = Number(itemTws);
          // Filter within ±2.5 of the selected bin value
          // Bin 10 includes values from 7.5 to 12.5 (exclusive on upper bound to avoid overlap)
          const minTws = selectedTws - 2.5;
          const maxTws = selectedTws + 2.5;
          return itemTwsNum >= minTws && itemTwsNum < maxTws;
        });
      } else {
        filteredData_tws = [];
      }
    }

    // Apply grade filter using selectedGrades array - use "greater than" logic
    // Use lowercase field names as primary with fallbacks for backward compatibility
    let filteredData: any[] = filteredData_tws;
    const grades = selectedGrades();
    if (grades.length > 0) {
      // For radio button behavior, only the first selected grade is used
      // Filter for grades greater than the selected grade
      const minGrade = Math.min(...grades.map(g => Number(g)).filter(g => !isNaN(g)));
      filteredData = filteredData_tws.filter((item: any) => {
        const itemGrade = item.grade ?? item.Grade ?? item.GRADE;
        if (itemGrade == null || itemGrade === undefined) return false;
        const numGrade = Number(itemGrade);
        if (isNaN(numGrade)) return false;
        return numGrade > minGrade;
      });
    }

    // Apply state filter using selectedStates array
    // Use lowercase field names as primary with fallbacks for backward compatibility
    const states = selectedStates();
    if (states.length > 0) {
      filteredData = filteredData.filter((item: any) => {
        const itemState = item.state ?? item.State ?? item.STATE;
        if (itemState == null || itemState === undefined) return false;
        const stateStr = String(itemState).trim();
        return states.some(selectedState => String(selectedState).trim().toLowerCase() === stateStr.toLowerCase());
      });
    }

    const races = selectedRaces();
    if (races.length > 0) {
      filteredData = filteredData.filter((item) => passesManeuverRaceFilter(item, races));
    }

    // Apply leg filter using selectedLegs array
    // Use lowercase field names as primary with fallbacks for backward compatibility
    const legs = selectedLegs();
    if (legs.length > 0) {
      filteredData = filteredData.filter((item: any) => {
        const legValue = item.leg_number ?? item.Leg_number ?? item.LEG;
        if (legValue == null || legValue === undefined) return false;
        return legs.includes(Number(legValue));
      });
    }

    const trainingRacing = maneuverTrainingRacing();
    if (trainingRacing) {
      filteredData = filteredData.filter((item) => passesManeuverTrainingRacingFilter(item, trainingRacing));
    }

    filteredData = applyManeuverEntrySide(filteredData, maneuverTackEntry()) as typeof filteredData;

    // If we have cut events, only show the cut maneuvers (by event id, or by time for a brush cut)
    if (isCut() && cutEvents().length > 0) {
      filteredData = applyCutToRows(filteredData, cutEvents());
    }

    // When VIDEO view: only show rows that have video (maneuver time falls inside a media window)
    if (view() === 'VIDEO') {
      const tz = getCurrentDatasetTimezone();
      const eventIdsWithVideo = new Set<number>();
      for (const row of filteredData) {
        const sourceName = row?.source_name ?? row?.sourceName ?? row?.Source_name ?? row?.source ?? '';
        if (typeof sourceName !== 'string' || !sourceName.trim()) continue;
        const raw = row?.datetime ?? row?.Datetime ?? row?.DATETIME ?? row?.date ?? row?.Date;
        if (raw == null || (typeof raw === 'string' && !raw.trim())) continue;
        let maneuverTime: Date;
        try {
          maneuverTime = typeof raw === 'string' ? new Date(raw.trim()) : new Date(raw);
          if (Number.isNaN(maneuverTime.getTime())) continue;
        } catch {
          continue;
        }
        if (mediaAvailabilityService.hasVideo(sourceName, maneuverTime, { timezone: tz, clipOffsetSeconds: MANEUVER_VIDEO_START_OFFSET_SECONDS })) {
          eventIdsWithVideo.add(row.event_id);
        }
      }
      filteredData = filteredData.filter((row: any) => eventIdsWithVideo.has(row.event_id));
    }

    const sortedRows = sortManeuverRows(filteredData, maneuverTableSort(), eventType());

    const eventIds = sortedRows.map((item: any) => item.event_id);
    const rowsSignature = eventIds.join(",");
    const prevRows = tabledata();
    const prevSignature = Array.isArray(prevRows)
      ? prevRows.map((item: { event_id?: number }) => item.event_id).join(",")
      : "";

    if (rowsSignature === prevSignature) {
      return;
    }

    setFiltered(eventIds)
    setTableData(sortedRows)
    
    // Update selection to match filtered data (but don't override user's active selection)
    if (!hasSelection()) {
      setSelection(eventIds as any)
    }

    // FleetManeuvers: always bump map/table after filter pass (sort-only changes still refresh)
    setTriggerUpdate(true);
  }

  useOpenGradeAllForMapClick({
    ready: fleetMountComplete,
    grades: selectedGrades,
    setSelectedGrades,
    reload: () => {
      void fetchTableData();
    },
  });

  const hasManeuvers = createMemo(() => {
    const maneuversData = maneuvers();
    return maneuversData && Array.isArray(maneuversData) && maneuversData.length > 0;
  });

  const hasFilteredManeuvers = createMemo(() => {
    const filteredIds = filtered();
    return filteredIds && Array.isArray(filteredIds) && filteredIds.length > 0;
  });

  const maneuverTimelinePoints = createMemo(() => {
    const rows = tabledata();
    if (!rows?.length) return [] as Record<string, unknown>[];
    return mapManeuverRowsToScatterTimeseriesPoints(rows as Record<string, unknown>[]);
  });

  const maneuverTimelineGroups = createMemo(() => {
    const rows = tabledata();
    return maneuverTimelineLegendGroups(rows as Record<string, unknown>[], color());
  });

  // Expensive pass: grouping + colours. Re-runs on data or colour-mode change only.
  const maneuverColorLegendBase = createMemo(() => {
    const rows = tabledata() as Record<string, unknown>[];
    // Touch the sources store so SOURCE colours repaint once the fleet colours load.
    if (String(color() || '').toUpperCase() === 'SOURCE') {
      sourcesStore.sources();
      sourcesStore.isReady();
    }
    return buildManeuverColorLegendItems(rows, color(), {
      eventType: eventType(),
      baseline: { name: maneuverBaselineLabel(), color: maneuverBaselineStore.baselineColor() },
    });
  });

  // Cheap pass: pill rings track the same selection the views highlight.
  const maneuverColorLegendItems = createMemo(() =>
    applyManeuverLegendSelection(maneuverColorLegendBase(), color(), {
      grouped: grouped(),
      selectedGroupKeys: grouped() ? selectedGroupKeys() : undefined,
      selectedEventIds: grouped() ? undefined : selectedEvents(),
    })
  );

  // The legend wraps, so its height changes with the colour mode — measured into the MAP/TABLE
  // height math rather than assumed.
  const [legendRowEl, setLegendRowEl] = createSignal<HTMLDivElement | undefined>(undefined);
  createEffect(() => {
    const el = legendRowEl();
    if (!el) return;
    const ro = new ResizeObserver(() => { updateContainerHeights(); });
    ro.observe(el);
    onCleanup(() => ro.disconnect());
  });

  // When table rows exist but the scatter mapper yields nothing, log one diagnostic row (time/id keys).
  createEffect(
    on(
      () => [tabledata().length, maneuverTimelinePoints().length] as const,
      ([tableLen, ptLen]) => {
        if (tableLen === 0 || ptLen > 0) return;
        const rows = tabledata();
        const sample = rows[0] as Record<string, unknown> | undefined;
        logDebug('FleetManeuvers: table rows present but zero timeline scatter points — check datetime/event_id on row shape', {
          tableLen,
          sampleKeys: sample ? Object.keys(sample).slice(0, 42) : [],
          event_id: sample?.event_id ?? sample?.Event_id,
          Datetime: sample?.Datetime,
          datetime: sample?.datetime,
          start_time: sample?.start_time,
        });
      }
    )
  );

  /** Reserve controls + map/table height and show strip whenever timeline is on and the table has rows (same as FleetManeuversHistory). */
  const showSelectionTimelineStrip = createMemo(() => showTimeline() && hasFilteredManeuvers());

  createEffect(() => {
    if (grouped() && view() === 'VIDEO') {
      setView('MAP');
      filterData();
    }
  });

  const handleTws = async (val: string): Promise<void> => {
    setTws(val)
    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
    await logActivity(project_id, dataset_id, 'FleetManeuvers.tsx', 'Fleet Maneuvers Report', `TWS changed to ${val}`);
    filterData()
  };


  const handleLegendClick = (legendItem: string) => {
    // In grouped mode, legend toggles group selection (same key format and toggle as grouped DataTable)
    if (grouped()) {
      const key = legendTextToGroupKeyTable(legendItem, color() || 'TWS');
      setSelectedGroupKeys((prev) => {
        const next = toggleGroupKeyInList(prev, key);
        setHasSelection(next.length > 0);
        return next;
      });
      setTriggerSelection(true);
      return;
    }

    // Use full maneuvers data for selection - don't filter by cuts when selecting
    const dataSource = maneuvers();
    if (!dataSource || !Array.isArray(dataSource) || dataSource.length === 0) {
      logDebug('FleetManeuvers: Legend click on', legendItem, '- no maneuvers data available');
      return;
    }

    const currentColorField = color();
    const clickedItem = String(legendItem);
    
    // Filter maneuvers by current color field matching the clicked legend item
    const legendEntryIds = dataSource
      .filter((m: any) => {
        const modeUpper = String(currentColorField || 'SOURCE').toUpperCase();

        if (currentColorField === 'GRADE') {
          const gradeValue = m.grade ?? m.Grade ?? m.GRADE;
          const clickedStr = clickedItem;
          const gradeNum = Number(gradeValue);
          const clickedNum = Number(clickedStr);
          if (!isNaN(gradeNum) && !isNaN(clickedNum) && gradeNum === clickedNum) return true;
          if (String(gradeValue) === clickedStr) return true;
          return false;
        }

        if (currentColorField === 'LEG') {
          const legValue = m.leg_number ?? m.Leg_number ?? m.LEG;
          const clickedStr = clickedItem;
          const legNum = Number(legValue);
          const clickedNum = Number(clickedStr);
          if (!isNaN(legNum) && !isNaN(clickedNum) && legNum === clickedNum) return true;
          if (String(legValue) === clickedStr) return true;
          return false;
        }

        if (currentColorField === 'TACK') {
          const tackValue = String(m.tack || '').toUpperCase();
          const clickedTack = clickedItem.toUpperCase();
          if ((tackValue === 'PORT' || tackValue === 'S - P') && (clickedTack === 'PORT' || clickedTack === 'S - P')) {
            return true;
          }
          if ((tackValue === 'STBD' || tackValue === 'P - S') && (clickedTack === 'STBD' || clickedTack === 'P - S')) {
            return true;
          }
          return false;
        }

        if (['TWS', 'RACE', 'SOURCE', 'STATE', 'CONFIG', 'TESTS', 'YEAR', 'EVENT'].includes(modeUpper)) {
          const targetKey = legendTextToGroupKeyTable(clickedItem, modeUpper);
          return groupKeyEquals(getGroupKeyFromItem(m, modeUpper), targetKey);
        }

        const fieldValueStr = m[currentColorField.toLowerCase()] ?? m[currentColorField];
        if (fieldValueStr === undefined || fieldValueStr === null) return false;
        if (String(fieldValueStr) === clickedItem) return true;
        const fieldNum = Number(fieldValueStr);
        const clickedNum = Number(clickedItem);
        if (!isNaN(fieldNum) && !isNaN(clickedNum) && fieldNum === clickedNum) return true;
        if (String(fieldValueStr).toLowerCase() === clickedItem.toLowerCase()) return true;
        return false;
      })
      .map((m: any) => m.event_id)
      .filter((id): id is number => id !== undefined && id !== null && !isNaN(Number(id)));

    if (legendEntryIds.length === 0) {
      logDebug('FleetManeuvers: Legend click on', legendItem, '- no matching event IDs found');
      return;
    }

    // Get current selection
    const currentSelected = selectedEvents();
    const currentSelectedSet = new Set(currentSelected);
    
    // Check if all legend entry IDs are already selected
    const allSelected = legendEntryIds.every(id => currentSelectedSet.has(id));
    
    if (allSelected) {
      // All IDs are selected → remove all legend entry IDs
      const updatedSelection = currentSelected.filter(id => !legendEntryIds.includes(id));
      if (updatedSelection.length > 0) {
        setLegendSelectionActive(true);
      }
      setSelectedEvents(updatedSelection);
      setHasSelection(updatedSelection.length > 0);
      logDebug('FleetManeuvers: Legend click on', legendItem, '- deselected all', legendEntryIds.length, 'event IDs');
    } else {
      // Not all IDs are selected → select all legend entry IDs (merge with existing)
      const updatedSelection = Array.from(new Set([...currentSelected, ...legendEntryIds]));
      setLegendSelectionActive(true);
      setSelectedEvents(updatedSelection);
      setHasSelection(updatedSelection.length > 0);
      logDebug('FleetManeuvers: Legend click on', legendItem, '- selected all', legendEntryIds.length, 'event IDs');
    }
    
    // Refresh selection styling without refetching timeseries data
    setTriggerSelection(true);
  };

  // Set dataset timezone when date changes so maneuver table datetime is in local time (dataset timezone)
  createEffect(async () => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const date = selectedDate();

    if (!className || !projectId || !date || String(date).trim() === "") {
      await setCurrentDataset(className || "", projectId || 0, null);
      return;
    }

    try {
      const ymd = String(date).replace(/[-/]/g, "");
      const timezoneResponse = await getData(
        `${apiEndpoints.app.datasets}/date/timezone?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&date=${encodeURIComponent(ymd)}`
      );

      const timezoneData = timezoneResponse?.data || timezoneResponse || {};
      const timezone = timezoneData.timezone;
      const datasetId = timezoneData.dataset_id;

      if (timezone && datasetId) {
        await setCurrentDataset(className, projectId, datasetId);
        logDebug("FleetManeuvers: Set timezone from date endpoint for table datetime", {
          datasetId,
          timezone: getCurrentDatasetTimezone(),
        });
      } else {
        await setCurrentDataset(className, projectId, null);
      }
    } catch (error: unknown) {
      logDebug("FleetManeuvers: Error setting timezone for date", error as any);
      await setCurrentDataset(className, projectId, null);
    }
  });

  // Initialize selectedSources from persistent settings
  createEffect(async () => {
    const sourcesReady = sourcesStore.isReady();
    const sources = sourcesStore.sources();
    const currentUser = user();
    
    if (!sourcesReady || !sources || sources.length === 0 || !currentUser?.user_id) {
      return;
    }
    
    // Only initialize once - check if already initialized
    if (selectedSources().size > 0) {
      return;
    }
    
    try {
      const { selectedClassName, selectedProjectId } = persistantStore;
      const className = selectedClassName();
      const projectId = selectedProjectId();
      
      if (className && projectId) {
        const settings = await persistentSettingsService.loadSettings(
          currentUser.user_id,
          className,
          projectId
        );
        
        if (settings?.fleetPerformanceSources && Array.isArray(settings.fleetPerformanceSources) && settings.fleetPerformanceSources.length > 0) {
          // Convert source names to source IDs
          const sourceIds = new Set<number>();
          settings.fleetPerformanceSources.forEach((sourceName: string) => {
            const source = sources.find((s: any) => s.source_name === sourceName);
            if (source && source.source_id) {
              sourceIds.add(source.source_id);
            }
          });
          
          if (sourceIds.size > 0) {
            setSelectedSources(sourceIds);
            debug('FleetManeuvers: Initialized sources from persistent settings', Array.from(sourceIds));
          } else {
            // No matching sources found - default to all sources
            const allSourceIds = new Set(sources.map((s: any) => s.source_id).filter((id: any) => id !== null && id !== undefined));
            setSelectedSources(allSourceIds);
            debug('FleetManeuvers: No matching sources found, defaulting to all sources');
          }
        } else {
          // No saved settings - default to all sources
          const allSourceIds = new Set(sources.map((s: any) => s.source_id).filter((id: any) => id !== null && id !== undefined));
          setSelectedSources(allSourceIds);
          debug('FleetManeuvers: No saved source settings, defaulting to all sources');
        }
      }
    } catch (error) {
      debug('FleetManeuvers: Error loading source settings, defaulting to all sources', error);
      // On error, default to all sources
      const allSourceIds = new Set(sources.map((s: any) => s.source_id).filter((id: any) => id !== null && id !== undefined));
      setSelectedSources(allSourceIds);
    }
  });

  // One debounced table refetch for: date, maneuver type, selected sources, training/racing. Waits until onMount
  // applied project filters so we do not race effects against loadFiltersFromPersistentSettings / getProjectManeuverFilters.
  createEffect(
    on(
      () => {
        const sources = sourcesStore.sources() ?? [];
        const allIds = sources
          .map((s: { source_id?: number }) => s.source_id)
          .filter((id: unknown): id is number => typeof id === 'number' && id > 0)
          .sort((a, b) => a - b);
        const sel = selectedSources();
        const isAll =
          sel.size === 0 ||
          (allIds.length > 0 &&
            sel.size === allIds.length &&
            allIds.every((id) => sel.has(id)));
        const selKey = isAll ? '__ALL__' : [...sel].sort((a, b) => a - b).join(',');

        return {
          mount: fleetMountComplete(),
          et: normalizeManeuverEventType(eventType()),
          date: (selectedDate() || '').trim(),
          ready: sourcesStore.isReady(),
          nsrc: sources.length,
          selKey,
          tr: maneuverTrainingRacing(),
        };
      },
      (cur, prev) => {
        if (!cur.mount) return;
        if (!cur.ready || cur.nsrc === 0 || !cur.date || !cur.et) return;

        const payload = { et: cur.et, date: cur.date, selKey: cur.selKey, tr: cur.tr };
        const prevPayload =
          prev && prev.mount ? { et: prev.et, date: prev.date, selKey: prev.selKey, tr: prev.tr } : null;
        if (prevPayload && JSON.stringify(payload) === JSON.stringify(prevPayload)) return;

        void refreshManeuverStateOptions();
        scheduleFleetTableFetch();
      },
      { defer: true }
    )
  );

  // React to cut events - refilter when cut state or cut payload changes
  createEffect(on([isCut, cutEvents], () => {
    if (untrack(() => maneuvers().length > 0)) {
      filterData();
    }
  }));

  // React to filter changes - refilter when filters change (client-side for grade/state/race/leg/training-racing)
  createEffect(on([selectedGrades, selectedStates, selectedRaces, selectedLegs, maneuverTackEntry, maneuverTrainingRacing], () => {
    if (untrack(() => maneuvers().length > 0)) {
      filterData();
    }
  }));

  // Training/racing is included in the unified fleet table fetch effect (tr in payload).

  // React to view and data: map/table layout is inside Show when filtered maneuvers exist, so we must
  // re-measure after async fetch (dataset Maneuvers runs fetch before first updateContainerHeights in onMount).
  createEffect(() => {
    view();
    filtered().length;
    tabledata().length;
    maneuvers().length;
    showTimeline();
    showSelectionTimelineStrip();
    // Colour mode changes the legend's wrapped height, so re-measure on it too.
    color();
    maneuverColorLegendItems().length;
    // Delegate to updateContainerHeights rather than repeating its arithmetic: this effect used to
    // carry a second copy, so anything added to the reserve there (e.g. the color legend row) was
    // silently overwritten here a tick later.
    setTimeout(() => {
      updateContainerHeights();
    }, 0);
  });

  // When VIDEO view and we have data + date, ensure media availability is preloaded then re-filter
  // Only run when view changes to VIDEO or when filters change the maneuvers data
  createEffect(on([view, maneuvers], ([currentView, data]) => {
    if (currentView !== 'VIDEO') return;
    const dateStr = untrack(() => selectedDate() ?? '');
    const dateYmd = dateStr.replace(/\D/g, '').slice(0, 8);
    if (dateYmd.length < 8) return;
    if (!data?.length) return;
    const seen = new Set<string>();
    const entries: Array<{ sourceName: string; dateYmd: string }> = [];
    for (const row of data) {
      const sn = row?.source_name ?? row?.sourceName ?? row?.Source_name ?? row?.source ?? '';
      if (typeof sn === 'string' && sn.trim() && !seen.has(sn.trim().toLowerCase())) {
        seen.add(sn.trim().toLowerCase());
        entries.push({ sourceName: sn.trim(), dateYmd });
      }
    }
    if (entries.length === 0) return;
    (async () => {
      try {
        await mediaAvailabilityService.preloadForSourcesAndDates(entries, untrack(() => getCurrentDatasetTimezone()));
        untrack(() => filterData());
      } catch (_) { /* empty */ }
    })();
  }));

  createEffect(() => {
    const ids = selectedEvents();
    void maneuvers();
    if (!Array.isArray(ids) || ids.length === 0) return;

    const className = selectedClassName();
    const projectId = selectedProjectId();
    const date = (selectedDate() || "").trim();
    if (!className || !projectId || !date) return;

    if (timesController) {
      timesController.abort();
    }
    timesController = new AbortController();
    const signal = timesController.signal;
    const datasetId = selectedDatasetId();
    const tableArr = Array.isArray(tabledata()) ? tabledata() : [];
    const maneuverArr = Array.isArray(maneuvers()) ? maneuvers() : [];
    const sources = sourcesStore.sources() ?? [];

    void runEventTimesSelectionSync({
      fetchTimes: async () => {
        if (datasetId && datasetId > 0) {
          return getEventTimes(
            {
              class_name: className,
              project_id: projectId,
              dataset_id: datasetId,
              event_list: ids,
            },
            signal
          );
        }

        if (sources.length === 0) {
          return null;
        }

        const bySource = new Map<number, number[]>();
        for (const eventId of ids) {
          const row = findManeuverRowByEventId(eventId, tableArr, maneuverArr);
          const sourceId = extractRowSourceId(row, sources);
          if (sourceId == null) continue;
          const list = bySource.get(sourceId) ?? [];
          if (!list.includes(eventId)) list.push(eventId);
          bySource.set(sourceId, list);
        }
        if (bySource.size === 0) {
          logDebug("FleetManeuvers: No source mapping for selected events — cannot fetch maneuver times");
          return null;
        }

        const chunks = await Promise.all(
          Array.from(bySource.entries()).map(async ([sourceId, eventList]) => {
            const result = await getEventTimes(
              {
                class_name: className,
                project_id: projectId,
                source_id: sourceId,
                date,
                event_list: eventList,
              },
              signal
            );
            return Array.isArray(result?.data) ? result.data : [];
          })
        );
        return { data: chunks.flat() };
      },
      applySelection: (result) => {
        if (result == null) return;
        setSelection((result as { data?: unknown }).data);
      },
    }).finally(() => {
      timesController = null;
    });
  });

  // Populate selectedRanges from maneuver event times + table rows (fleet multi-dataset; selectionStore skips getEventTimeRanges on this page).
  let lastFleetManeuverRangesSig = "";
  createEffect(() => {
    const ids = selectedEvents();
    const selectionRows = selection();
    void maneuvers();
    if (!Array.isArray(ids) || ids.length === 0) {
      lastFleetManeuverRangesSig = "";
      // Clear explicitly; the store's follow-up skips its clear while a brush is active.
      if (untrack(() => selectedRanges()).length > 0) {
        setSelectedRanges([]);
      }
      return;
    }
    const tableArr = Array.isArray(tabledata()) ? tabledata() : [];
    const maneuverArr = Array.isArray(maneuvers()) ? maneuvers() : [];
    const lookupRows =
      maneuverArr.length > 0
        ? [
            ...maneuverArr,
            ...tableArr.filter(
              (row: { event_id?: number }) =>
                !maneuverArr.some((m: { event_id?: number }) => m?.event_id == row?.event_id)
            ),
          ]
        : tableArr;

    const sources = sourcesStore.sources() ?? [];
    const sourceColorScale = fleetManeuverSourceColorScale();
    const resolveColor: SelectionRangeColorResolver = (row) => {
      const name = extractRowSourceName(row);
      if (sourceColorScale && name) {
        return sourceColorScale(name.toLowerCase());
      }
      return undefined;
    };

    const selectionTimeRows = Array.isArray(selectionRows)
      ? selectionRows.filter(
          (row) => row != null && typeof row === "object" && !Array.isArray(row) && "start_time" in row
        )
      : [];

    if (lookupRows.length === 0 && selectionTimeRows.length === 0) return;

    // Per-id build so ids the async getEventTimes fetch hasn't returned yet still get a range from their
    // table row, instead of the partial list that used to be frozen in by the signature dedup below.
    const ranges = buildSelectedRangesForEvents({
      eventIds: ids,
      selectionData: selectionTimeRows,
      lookupRows,
      resolveColor,
      sources,
    });
    if (ranges.length === 0) return;

    const sig = selectionRangesSignatureForEvents(ids, ranges);
    if (sig === lastFleetManeuverRangesSig) return;
    lastFleetManeuverRangesSig = sig;
    setSelectedRanges(ranges);
  });

  // Function to sync table height with map SVG height
  const syncTableHeightWithMap = (): void => {
    if (view() !== 'MAP') return;
    
    const mapElement = document.querySelector('#maneuver-map') as HTMLElement | null;
    const tableArea = document.getElementById('table-area') as HTMLElement | null;
    
    if (mapElement && tableArea) {
      // Get the actual height of the map element (including SVG)
      const mapHeight = mapElement.offsetHeight || mapElement.clientHeight;
      
      // Set table area to match map height
      if (mapHeight > 0) {
        tableArea.style.height = `${mapHeight}px`;
        tableArea.style.minHeight = `${mapHeight}px`;
      }
    }
  };

  const syncTableHeightWithVideo = (): void => {
    if (view() !== 'VIDEO') return;
    const layout = document.querySelector('.maneuver-map-layout') as HTMLElement | null;
    const tableArea = document.getElementById('table-area') as HTMLElement | null;
    if (layout && tableArea) {
      const layoutHeight = layout.offsetHeight || layout.clientHeight;
      if (layoutHeight > 0) {
        tableArea.style.height = `${layoutHeight}px`;
        tableArea.style.minHeight = `${layoutHeight}px`;
      }
    }
  };

  // Function to update container heights based on actual viewport (full screen)
  const updateContainerHeights = (): void => {
    // Reserve strip height when timeline is on and filtered table has rows (matches history page)
    const headerHeight = 58;
    const timelineOn = showSelectionTimelineStrip();
    // Measured, not a constant: the legend row wraps to a different height per colour mode.
    const legendReservePx = maneuverColorLegendReservePx();

    const additionalSpacingForMap = (timelineOn ? 218 : -50) + legendReservePx;
    const mapHeight = window.innerHeight - headerHeight - additionalSpacingForMap;

    const additionalSpacingForTable = (timelineOn ? 248 : 128) + legendReservePx;
    const tableHeight = window.innerHeight - headerHeight - additionalSpacingForTable;
    
    const mapLayout = document.querySelector('.maneuver-map-layout') as HTMLElement | null;
    const tableWrapper = document.getElementById('datatable-big-wrapper') as HTMLElement | null;
    
    if (mapLayout) {
      mapLayout.style.height = `${mapHeight}px`;
      mapLayout.style.minHeight = `${mapHeight}px`;
      mapLayout.style.maxHeight = `${mapHeight}px`;
    }
    
    if (tableWrapper) {
      tableWrapper.style.height = `${tableHeight}px`;
      tableWrapper.style.maxHeight = `${tableHeight}px`;
      tableWrapper.style.overflow = 'hidden';
    }
    
    setTimeout(() => {
      if (view() === 'MAP') syncTableHeightWithMap();
      if (view() === 'VIDEO') syncTableHeightWithVideo();
    }, 100);
  };
  
  // Function to observe sidebar for collapse/expand changes
  const observeSidebar = (): MutationObserver | null => {
    const sidebar = document.querySelector('.sidebar');
    if (!sidebar) return null;
    
    const observer = new MutationObserver(() => {
      // When sidebar class changes (collapsed/expanded), update heights
      updateContainerHeights();
    });
    
    observer.observe(sidebar, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return observer;
  };

  // Load filters from persistent settings
  const loadFiltersFromPersistentSettings = async () => {
    const currentUser = user();
    if (currentUser?.user_id) {
      try {
        const { selectedClassName, selectedProjectId } = persistantStore;
        const className = selectedClassName();
        const projectId = selectedProjectId();

        if (className && projectId) {
          const settings = await persistentSettingsService.loadSettings(
            currentUser.user_id,
            className,
            projectId
          );

          logDebug('FleetManeuvers: Loaded settings from persistent settings', settings);
          let gradesLoaded = false;
          if (settings?.maneuverFilters) {
            const filters = settings.maneuverFilters;
            if (filters.grades && Array.isArray(filters.grades) && filters.grades.length > 0) {
              setSelectedGrades(filters.grades);
              setSelectedGradesManeuvers(filters.grades.map((g: number) => String(g)));
              gradesLoaded = true;
              logDebug('FleetManeuvers: Loaded grade filters from persistent settings', filters.grades);
            }
            // filters.states is deliberately NOT read here. States are per maneuver type now
            // (useManeuverStatesForEventType), and this flat value is only the first-visit seed that
            // hook falls back to.
            if (filters.races && Array.isArray(filters.races) && filters.races.length > 0) {
              setSelectedRaces(filters.races);
              setSelectedRacesManeuvers(filters.races.map((r: number | string) => String(r)));
              logDebug('FleetManeuvers: Loaded race filters from persistent settings', filters.races);
            }
            if (filters.legs && Array.isArray(filters.legs) && filters.legs.length > 0) {
              setSelectedLegs(filters.legs);
              setSelectedLegsManeuvers(filters.legs.map((l: number) => String(l)));
              logDebug('FleetManeuvers: Loaded leg filters from persistent settings', filters.legs);
            }
            if (filters.trainingRacing === 'TRAINING' || filters.trainingRacing === 'RACING') {
              setManeuverTrainingRacing(filters.trainingRacing);
              logDebug('FleetManeuvers: Loaded trainingRacing filter from persistent settings', filters.trainingRacing);
            }
            if (filters.tackEntry === 'PORT' || filters.tackEntry === 'STBD') {
              setManeuverTackEntry(filters.tackEntry);
              logDebug('FleetManeuvers: Loaded tack entry filter from persistent settings', filters.tackEntry);
            }
          } else {
            logDebug('FleetManeuvers: No maneuverFilters found in settings', settings);
          }
          
          // Set default grade >1 (shows grades 2 and 3) if no grades were loaded from persistent settings
          if (!gradesLoaded) {
            setSelectedGrades([1]);
            setSelectedGradesManeuvers(['1']);
            logDebug('FleetManeuvers: Set default grade filter to [1] (>1)');
          }
        }
      } catch (error) {
        logDebug('FleetManeuvers: Error loading filters from persistent settings:', error);
        if (selectedGrades().length === 0) {
          setSelectedGrades([1]);
          setSelectedGradesManeuvers(['1']);
          logDebug('FleetManeuvers: Set default grade filter to [1] (>1) after error');
        }
      }
    } else {
      if (selectedGrades().length === 0) {
        setSelectedGrades([1]);
        setSelectedGradesManeuvers(['1']);
        logDebug('FleetManeuvers: Set default grade filter to [1] (>1) (no user)');
      }
    }
  };

  onMount(async () => {
    // Teardown holders + onCleanup are registered synchronously, BEFORE the awaits below. Solid
    // only has an owner synchronously, so an onCleanup placed next to the resources (created after
    // those awaits) silently never runs — and the disposer this function used to `return` was
    // discarded outright, because Solid ignores onMount return values. The result was one leaked
    // scaling teardown, two ResizeObservers, a MutationObserver, a 100 ms interval and two window
    // listeners per mount.
    let cleanupScaling: (() => void) | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let mapObserver: ResizeObserver | null = null;
    let sidebarObserver: MutationObserver | null = null;
    let sidebarCheckInterval: ReturnType<typeof setInterval> | null = null;
    let handleResize: (() => void) | null = null;
    let handleKeyDown: ((event: KeyboardEvent) => void) | null = null;

    onCleanup(() => {
      cleanupScaling?.();
      resizeObserver?.disconnect();
      mapObserver?.disconnect();
      sidebarObserver?.disconnect();
      if (sidebarCheckInterval) clearInterval(sidebarCheckInterval);
      if (handleResize) window.removeEventListener('resize', handleResize);
      if (handleKeyDown) window.removeEventListener('keydown', handleKeyDown);
    });
    resetManeuverTableSort();
    const restored = willRestoreShare(FLEET_MANEUVERS_PAGE_KEY)
      ? await shareRestoreReady(FLEET_MANEUVERS_PAGE_KEY)
      : false;
    await logPageLoad('FleetManeuvers.tsx', 'Fleet Maneuvers Analysis Report');
    
    // Wait for sources to be ready (poll until ready)
    while (!sourcesStore.isReady()) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    const sharedBaseline = consumeShareSpecifiedBaseline(FLEET_MANEUVERS_PAGE_KEY);
    if (!restored || !sharedBaseline) {
      await baselineDisplay.restoreSavedBaseline();
    }
    if (!restored) {
      // Set default color to SOURCE for fleet context; default TWS to ALL
      setColor('SOURCE');
      setEventType("TACK")
      setView("MAP")
      setTws('ALL')
    }

    const className = selectedClassName();
    const projectId = selectedProjectId();
    const dateForFilters = (selectedDate() || '').trim() || undefined;
    // A share already carried TWS, the baseline and these filters. Loading the viewer's defaults
    // on top is what made a shared day look like a fresh page.
    if (!restored) {
      const projectFilters = (className && projectId) ? await getProjectManeuverFilters(className, projectId, dateForFilters) : null;
      if (projectFilters === null) {
        await loadFiltersFromPersistentSettings();
      }

      // Set in-report defaults first (when none are set)
      if (!selectedGrades().length) {
        setSelectedGrades([1]);
        setSelectedGradesManeuvers(['1']);
      }

      // Apply project filters on top (overrides defaults; empty array means "no filter")
      if (projectFilters !== null) {
        if (projectFilters.grades !== undefined) {
          const grades = Array.isArray(projectFilters.grades) ? projectFilters.grades : [];
          setSelectedGrades(grades);
          setSelectedGradesManeuvers(grades.map(g => String(g)));
          logDebug('FleetManeuvers: Applied grade filters from project default', projectFilters.grades);
        }
        if (projectFilters.states !== undefined) {
          // A first-visit seed, not an override: the project holds one flat list for every maneuver
          // type, so it yields to this user's own per-type chips. See applyProjectDefaultStates.
          await applyProjectDefaultStates(projectFilters.states);
          logDebug('FleetManeuvers: Offered state filters from project default', projectFilters.states);
        }
      }
    }
    // Fetch timeseries description options

    // Allow the single unified createEffect to run (debounced); avoids overlapping fetches with effects during this async onMount.
    setFleetMountComplete(true);
    
    // Set up dynamic scaling for media-container using the global utility
    cleanupScaling = setupMediaContainerScaling({
      logPrefix: 'FleetManeuvers'
    });
    
    // Set initial heights
    updateContainerHeights();
    
    // Update heights on container resize (scaling is handled by setupMediaContainerScaling)
    resizeObserver = new ResizeObserver(() => {
      updateContainerHeights();
    });
    
    // Observe map element to sync table height when map resizes
    mapObserver = new ResizeObserver(() => {
      if (view() === 'MAP') {
        syncTableHeightWithMap();
      }
    });
    
    const mediaContainer = document.getElementById('media-container');
    if (mediaContainer) {
      // Observe the appropriate reference container (split panel or parent)
      const splitPanel = mediaContainer.closest('.split-panel');
      const mainContent = mediaContainer.closest('#main-content');
      const reference = splitPanel || mainContent || mediaContainer.parentElement;
      if (reference) {
        resizeObserver.observe(reference);
      }
    }
    
    // Observe map element for height changes
    const mapElement = document.querySelector('#maneuver-map');
    if (mapElement) {
      mapObserver.observe(mapElement);
    }
    
    // Also observe map area container
    const mapArea = document.getElementById('map-area');
    if (mapArea) {
      mapObserver.observe(mapArea);
    }
    
    // Observe sidebar for collapse/expand changes
    sidebarObserver = observeSidebar();
    
    // Poll for sidebar changes (since same-tab localStorage changes don't trigger storage event)
    sidebarCheckInterval = setInterval(() => {
      const sidebar = document.querySelector('.sidebar');
      if (sidebar) {
        const isCollapsed = sidebar.classList.contains('collapsed');
        const wasCollapsed = localStorage.getItem('_sidebarWasCollapsed') === 'true';
        if (isCollapsed !== wasCollapsed) {
          localStorage.setItem('_sidebarWasCollapsed', isCollapsed.toString());
          updateContainerHeights();
        }
      }
    }, 100);
    
    // Also listen to window resize for when the main window changes size
    handleResize = (): void => {
      updateContainerHeights();
    };
    window.addEventListener('resize', handleResize);

    // 'h' key: Hide selected events; 'x' clear; 'c' cut
    handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (document.querySelector('.pagesettings-modal')) return;

      if (event.key === 'x' || event.key === 'X') {
        event.preventDefault();
        logDebug('FleetManeuvers: Clearing selection (x key pressed)');
        pendingShowMeTeams = null;
        pendingScrollToEvidence = false;
        clearSelection();
        requestAnimationFrame(() => setTriggerUpdate(true));
        return;
      }

      if (event.key === 'c' || event.key === 'C') {
        event.preventDefault();
        const currentSelected = selectedEvents();
        if (currentSelected && currentSelected.length > 0) {
          logDebug('FleetManeuvers: Cutting selection (c key pressed)');
          cutSelection();
          requestAnimationFrame(() => setTriggerUpdate(true));
        }
        return;
      }

      if (event.key === 'h' || event.key === 'H') {
        const currentSelected = selectedEvents();
        if (currentSelected && currentSelected.length > 0) {
          event.preventDefault();
          hideSelectedEvents();
          requestAnimationFrame(() => setTriggerUpdate(true));
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    
    // Teardown is registered by the onCleanup at the top of this onMount (see the note there).
  });

  // Cleanup abort controllers and other resources on component unmount
  onCleanup(() => {
    if (fleetTableFetchDebounce) {
      clearTimeout(fleetTableFetchDebounce);
      fleetTableFetchDebounce = null;
    }
    // Abort any pending table data requests
    if (tableDataController) {
      tableDataController.abort();
      tableDataController = null;
    }
    
    // Abort any pending times requests
    if (timesController) {
      timesController.abort();
      timesController = null;
    }
    
    // Clear any stored data to free memory
    setManeuvers([]);
    setTableData([]);
    setFiltered([]);
    
    logDebug('FleetManeuvers: Cleanup complete - abort controllers cleared and data reset');
  });

  const toggleRaceFilter = (race: number | string) => {
    const current = selectedRaces();
    const next = current.includes(race) ? current.filter(r => r !== race) : [...current, race];
    setSelectedRaces(next);
    setSelectedRacesManeuvers(next.map(r => String(r)));
  };

  const toggleLegFilter = (leg: number) => {
    const current = selectedLegs();
    const next = current.includes(leg) ? current.filter(l => l !== leg) : [...current, leg];
    setSelectedLegs(next);
    setSelectedLegsManeuvers(next.map(l => String(l)));
  };

  const toggleGradeFilter = (grade: number) => {
    const current = selectedGrades();
    const next = current.includes(grade) ? [] : [grade];
    setSelectedGrades(next);
    setSelectedGradesManeuvers(next.map(g => String(g)));
  };

  const toggleStateFilter = (state: string) => {
    const current = selectedStates();
    const next = current.includes(state) ? current.filter(s => s !== state) : [...current, state];
    setSelectedStates(next);
    setSelectedStatesManeuvers(next);
    // Remembered against the maneuver type on screen, so a type switch brings back its own chips.
    persistStates();
  };

  createEffect(() => {
    const rLocal = raceOptions().map((x) => String(x));
    const lLocal = legOptions().map((x) => String(x));
    const gLocal = gradeOptions().map((x) => String(x));
    const rs = storeRaceOptions().map((x) => String(x));
    const ls = storeLegOptions().map((x) => String(x));
    const gs = storeGradeOptions().map((x) => String(x));
    if (JSON.stringify([...rLocal].sort()) !== JSON.stringify([...rs].sort())) {
      pushRaceOptionsToFilterStore(rLocal);
    }
    if (JSON.stringify([...lLocal].sort()) !== JSON.stringify([...ls].sort())) {
      pushLegOptionsToFilterStore(lLocal);
    }
    if (JSON.stringify([...gLocal].sort()) !== JSON.stringify([...gs].sort())) {
      pushGradeOptionsToFilterStore(gLocal);
    }
  });

  // Create dataSourcesOptions from sourcesStore
  const dataSourcesOptions = createMemo(() => {
    return sourcesStore.sources().map(source => ({
      key: `source-${source.source_id}`,
      label: source.source_name,
      signal: [
        () => selectedSources().has(source.source_id),
        (value: boolean) => {
          const current = selectedSources();
          const next = new Set(current);
          if (value) {
            next.add(source.source_id);
          } else {
            next.delete(source.source_id);
          }
          setSelectedSources(next);
        }
      ] as [() => boolean, (value: boolean) => void]
    }));
  });

  const insightFindings = createMemo(() => {
    const home = resolveHomeBoatName();
    persistantStore.homeBoatName();
    if (!home) return [];
    const rows = (tabledata() ?? []) as Record<string, unknown>[];
    const et = String(eventType() || 'TACK').toUpperCase();
    return buildManeuverFindings({ rows, eventType: et }, home).slice(0, 12);
  });

  const insightFleetSize = createMemo(() => {
    const rows = (tabledata() ?? []) as Record<string, unknown>[];
    const names = new Set<string>();
    for (const row of rows) {
      const n = String(
        row.source_name ?? row.TEAM ?? row.Source_name ?? ''
      ).trim().toLowerCase();
      if (n) names.add(n);
    }
    return names.size;
  });

  const handleInsightShowMe = (action: ShowMeAction) => {
    const teams = (action.highlightTeams ?? [])
      .map((t) => String(t).trim())
      .filter(Boolean);

    // Clear then re-select so Show me always replaces the previous highlight set.
    clearSelection({ preserveFilters: true, immediate: true });
    setSelectedGroupKeys([]);
    pendingShowMeTeams = teams.length > 0 ? teams : null;
    pendingScrollToEvidence = true;

    const nextType = action.eventType
      ? normalizeManeuverEventType(action.eventType)
      : null;
    const typeChanging =
      nextType != null && nextType !== normalizeManeuverEventType(eventType());

    if (action.eventType) {
      handleEventType(action.eventType);
    }
    if (action.sortKey) {
      setManeuverTableSort({
        key: action.sortKey,
        direction: action.sortDirection ?? 'asc',
      });
    }

    if (!typeChanging) {
      // Snapshot rows now; apply on the next macrotask so selectionStore's
      // isUpdatingSelectedEvents guard (left true by clearSelection's empty write) has reset.
      const tableRows = tabledata();
      const rows = (
        Array.isArray(tableRows) && tableRows.length > 0
          ? tableRows
          : (maneuvers() ?? [])
      ) as Record<string, unknown>[];
      setTimeout(() => {
        if (!pendingShowMeTeams) {
          return;
        }
        applyShowMeTeamSelection(rows);
        pendingScrollToEvidence = false;
        scrollToEvidence(
          "[data-fleet-maneuvers-page] .maneuver-map-layout, [data-fleet-maneuvers-page] #table-area"
        );
      }, 0);
    }
    // Event-type change: applyShowMeTeamSelection runs after fetchTableData completes.
  };

  return (
    <div
      id="media-container"
      class={`maneuvers-page${showSelectionTimelineStrip() ? ' maneuvers-page--with-selection-timeline' : ''}`}
      data-fleet-maneuvers-page
    >
      <div class="container">
        {/* Controls always visible so user can change filters even when no data */}
        <div id="maneuver-controls" class="flex flex-col w-full pl-2 pt-2">
            {/* Top Control Bar: single row */}
            <div class="maneuver-control-bar-row flex w-full h-[50px] items-center gap-x-2">
              {/* Left group: 4 buttons */}
              <div class="flex gap-x-2 items-center flex-shrink-0">
                <div class="self-center">
                  <ManeuverSettings
                    useIconTrigger={true}
                    isHistoryPage={false}
                    raceOptions={raceOptions()}
                    legOptions={legOptions()}
                    gradeOptions={gradeOptions()}
                    stateOptions={stateOptions()}
                    selectedRaces={() => selectedRaces()}
                    selectedLegs={() => selectedLegs()}
                    selectedGrades={() => selectedGrades()}
                    selectedStates={() => selectedStates()}
                    selectedTrainingRacing={maneuverTrainingRacing()}
                    onTrainingRacingFilterChange={setManeuverTrainingRacing}
                    selectedTackEntry={maneuverTackEntry()}
                    onTackEntryFilterChange={setManeuverTackEntry}
                    setRaceOptions={setRaceOptions}
                    setLegOptions={setLegOptions}
                    setGradeOptions={setGradeOptions}
                    setStateOptions={setStateOptions}
                    toggleRaceFilter={toggleRaceFilter}
                    toggleLegFilter={toggleLegFilter}
                    toggleGradeFilter={toggleGradeFilter}
                    toggleStateFilter={toggleStateFilter}
                    onApplyFilters={() => fetchTableData()}
                    useUnfilteredOptions={true}
                    includeAllGradeOption={true}
                    dataSourcesOptions={dataSourcesOptions()}
                    showTimeline={() => showTimeline()}
                    onTimelineChange={handleTimelineChange}
                    componentConfig={{
                      showGrades: true,
                      showRaces: true,
                      showLegs: true,
                      showStates: true,
                      showTackEntry: true,
                    }}
                    onSelectBaseline={() => void baselineDisplay.openModal()}
                    baselineName={baselineDisplay.baselineLabel}
                  />
                </div>
                <DropDownButton 
                  options={eventTypes()}
                  defaultText={eventType()}
                  smallLabel="Maneuver"
                  size="big"
                  handleSelection={handleEventType}
                />
                <DropDownButton
                  options={viewOptions()}
                  defaultText={grouped() && view() === 'SCATTER' ? 'BOXES' : view()}
                  smallLabel="View"
                  size="big"
                  handleSelection={handleView}
                />
                <Show when={view() === 'TIME SERIES' || view() === 'MAP' || view() === 'VIDEO'}>
                  <DropDownButton
                    options={phases()}
                    defaultText={phase()}
                    smallLabel="Phase"
                    size="big"
                    handleSelection={handlePhase}
                  />
                </Show>
                {/* Active Filters: right side of left button area */}
                <div class="maneuver-panel-filter-summary">
                  <PerformanceFilterSummary
                    filterGrades={selectedGrades().join(",")}
                    filterState={selectedStates().join(",")}
                    stateOptions={stateOptions()}
                    trainingRacing={maneuverTrainingRacing()}
                    tackEntry={maneuverTackEntry()}
                    gradeAsGreaterThan
                  />
                </div>
              </div>

              {/* Right button area: TWS, Grouped, Data, Color By (unchanged) */}
              <div class="maneuver-control-bar-right flex gap-x-2 items-center ml-auto flex-shrink-0">
                <DropDownButton
                  options={twsoptions()}
                  defaultText={tws()}
                  smallLabel="Tws"
                  size="small"
                  handleSelection={handleTws}
                />
                <DropDownButton
                  options={groupedOptions()}
                  defaultText={groupDisplayMode()}
                  smallLabel="Group"
                  size="small"
                  handleSelection={(val) => { setGroupDisplayMode(val); setTriggerUpdate(true); }}
                />
                <Show when={view() === 'MAP' || view() === 'TIME SERIES' || view() === 'SCATTER' || view() === 'VIDEO'}>
                  <DropDownButton
                    options={colors()}
                    defaultText={color()}
                    smallLabel="Color By"
                    size="big"
                    handleSelection={handleColor}
                  />
                </Show>
              </div>
            </div>
            
            <Show when={showSelectionTimelineStrip()}>
              <div class="maneuver-selection-timeline">
                <ScatterTimeseries
                  key={`fleet-maneuver-tl-${maneuverTimelinePoints().length}-${color()}-${tws()}-${eventType()}`}
                  aggregates={maneuverTimelinePoints()}
                  color={color()}
                  groups={maneuverTimelineGroups()}
                  isHistoryPage={false}
                  showUwDw={false}
                  skipSelectedSourcesFilter={true}
                  colorScale={
                    color() === 'SOURCE' && fleetManeuverSourceColorScale()
                      ? (source: string) => String(fleetManeuverSourceColorScale()!(source))
                      : undefined
                  }
                />
              </div>
            </Show>
            {/* Shared color-by legend for all views. Deliberately outside the strip's Show: it
                stays visible when the Timeline toggle is off, so MAP/TABLE keep their legend. */}
            <Show when={hasFilteredManeuvers() && maneuverColorLegendItems().length > 0}>
              <div
                id="maneuver-color-legend-row"
                class="maneuver-color-legend-row"
                ref={setLegendRowEl}
              >
                <ManeuverColorLegend
                  colorMode={color()}
                  items={maneuverColorLegendItems()}
                  onItemClick={handleLegendClick}
                  onBaselineClick={() => void baselineDisplay.openModal()}
                />
              </div>
            </Show>
        </div>
        {/* Content section: empty state when no data, then filter-too-strict or views */}
        <Show when={!hasManeuvers()} fallback={null}>
          <div class="flex flex-col items-center justify-center w-full min-h-[40vh] px-4 text-gray-600">
            <p class="text-lg font-medium">No maneuvers to display</p>
            <p class="text-sm mt-2 text-center max-w-md">
              Select a date and ensure sources are available. Adjust filters above and apply to reload.
            </p>
          </div>
        </Show>
        <Show when={hasManeuvers() && !hasFilteredManeuvers()}>
            <div class="flex items-center justify-center h-[calc(100vh-200px)]">
              <div class="text-center">
                <p class="text-xl text-gray-600">No maneuvers found</p>
                <p class="text-sm text-gray-500 mt-1">Filtering criteria may be too strict, check your settings...</p>
              </div>
            </div>
          </Show>
          <Show when={hasFilteredManeuvers()}>
            <div class="px-2 pt-2">
              <InsightPanel
                areaLabel={`${String(eventType() || 'maneuver').toLowerCase()}s`}
                findings={insightFindings}
                fleetSize={insightFleetSize}
                onShowMe={handleInsightShowMe}
              />
            </div>
            <Show when={view() === 'MAP'}>
              <div
                id="maneuver-map-layout"
                class="maneuver-map-layout flex w-full"
                classList={{ 'maneuver-map-layout--chart-panel': mapSidePanel() === 'CHART' }}
              >
                <div id="table-area">
                  <Show when={!grouped()}>
                    <Show when={mapSidePanel() === 'TABLE'}>
                      <DataTable_Small context={context} />
                      <Show when={selectedEvents().length > 0}>
                        <div class="flex justify-start mt-2">
                          <button
                            class="btn"
                            type="button"
                            onClick={() => setMapSidePanel('CHART')}
                          >
                            Show Timeseries →
                          </button>
                        </div>
                      </Show>
                    </Show>
                    <Show when={mapSidePanel() === 'CHART'}>
                      <div style={{ height: "100%", display: "flex", "flex-direction": "column", "min-height": "0" }}>
                        <div style={{ flex: "0 0 auto", "min-height": "0" }}>
                          <TimeSeries context={context} selectedOnly playhead fitParent />
                        </div>
                        <div class="flex justify-start" style={{ "flex-shrink": "0", "margin-top": `${MAP_SIDE_PANEL_EXTRA_HEIGHT_PX + MAP_SIDE_PANEL_BUTTON_GAP_PX}px` }}>
                          <button class="btn" type="button" onClick={() => setMapSidePanel('TABLE')}>
                            ← Show Data Table
                          </button>
                        </div>
                      </div>
                    </Show>
                  </Show>
                  <Show when={grouped()}>
                    <DataTable_SmallGrouped context={context} />
                  </Show>
                </div>
                <div id="map-area">
                  <Show when={!grouped()}>
                    <ManeuverMap context={context} />
                  </Show>
                  <Show when={grouped()}>
                    <MapGrouped context={context} />
                  </Show>
                </div>
              </div>
            </Show>
            <Show when={view() === 'VIDEO'}>
              <div id="maneuver-map-layout" class="maneuver-map-layout flex w-full">
                <div id="table-area">
                  <Show when={!grouped()}>
                    <Show when={videoSidePanel() === 'TABLE'}>
                      <DataTable_Small context={context} useSelectionIndexColors={true} />
                      <Show when={selectedEvents().length > 0}>
                        <div class="flex justify-start mt-2">
                          <button
                            class="btn"
                            type="button"
                            onClick={() => setVideoSidePanel('CHART')}
                          >
                            Show Timeseries →
                          </button>
                        </div>
                      </Show>
                    </Show>
                    <Show when={videoSidePanel() === 'CHART'}>
                      <div style={{ height: "100%", display: "flex", "flex-direction": "column", "min-height": "0" }}>
                        <div style={{ flex: "1 1 auto", "min-height": "0" }}>
                          <TimeSeries context={context} selectedOnly playhead fitParent />
                        </div>
                        <div class="flex justify-start mt-2" style={{ "flex-shrink": "0" }}>
                          <button class="btn" type="button" onClick={() => setVideoSidePanel('TABLE')}>
                            ← Show Data Table
                          </button>
                        </div>
                      </div>
                    </Show>
                  </Show>
                  <Show when={grouped()}>
                    <DataTable_SmallGrouped context={context} />
                  </Show>
                </div>
                <div id="map-area">
                  <Video context={context} onDataUpdate={fetchTableData} />
                </div>
              </div>
            </Show>
            <Show when={view() === 'SCATTER'}>
              <div id="scatter-area">
                <Show when={!grouped()}>
                  <Scatter context={context} />
                </Show>
                <Show when={grouped()}>
                  <ScatterGrouped context={context} />
                </Show>
              </div>
            </Show>
            <Show when={view() === 'TIME SERIES'}>
              <div id="timeseries-area">
                <Show when={!grouped()}>
                  <TimeSeries context={context} onDataUpdate={fetchTableData} />
                </Show>
                <Show when={grouped()}>
                  <TimeSeriesGrouped context={context} onDataUpdate={fetchTableData} />
                </Show>
              </div>
            </Show>
            <Show when={view() === 'TABLE'}>
              <div id="datatable-big-wrapper">
                <Show when={!grouped()}>
                  <DataTable_Big context={context} />
                </Show>
                <Show when={grouped()}>
                  <DataTable_BigGrouped context={context} />
                </Show>
              </div>
            </Show>
          </Show>
      </div>
      <BaselineDisplayModal
        show={baselineDisplay.showModal()}
        onClose={baselineDisplay.closeModal}
        title="Select Baseline"
        baselines={baselineDisplay.availableBaselines()}
        selectedKey={baselineDisplay.selectedKey()}
        onSelectedKeyChange={baselineDisplay.setSelectedKey}
        configs={baselineDisplay.modalConfigs()}
        selectedConfig={baselineDisplay.modalConfig()}
        onSelectedConfigChange={baselineDisplay.setModalConfig}
        baselineColor={baselineDisplay.modalColor()}
        onBaselineColorChange={baselineDisplay.setModalColor}
        loading={baselineDisplay.loading()}
        loadingAction={baselineDisplay.loadingAction()}
        error={baselineDisplay.error()}
        onConfirm={() => void baselineDisplay.handleConfirm()}
        onRemove={() => void baselineDisplay.handleRemove()}
      />
    </div>
  );
}
